import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { api, formatError } from "../lib/api";
import { logger } from "../lib/logger";
import { Flame, Loader2, TrendingUp, Zap, Clock } from "lucide-react";

const PLATFORMS = ["tiktok", "instagram", "youtube", "twitter", "linkedin"];

export default function TrendRadarPage() {
  const nav = useNavigate();
  const [platform, setPlatform] = useState("tiktok");
  const [niche, setNiche] = useState("");
  const [trends, setTrends] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [loadingT, setLoadingT] = useState(false);
  const [loadingI, setLoadingI] = useState(false);

  const loadTrends = async () => {
    setLoadingT(true);
    setTrends([]);
    try {
      const { data } = await api.get("/trends", { params: { platform, niche } });
      setTrends(data.trends || []);
    } catch (e) {
      logger.error("Trends error", e);
      toast.error(formatError(e.response?.data?.detail) || "Trends unavailable");
    } finally {
      setLoadingT(false);
    }
  };

  const loadIdeas = async () => {
    setLoadingI(true);
    setIdeas([]);
    try {
      const { data } = await api.post("/daily-ideas", { platform, niche, count: 3 });
      setIdeas(data.ideas || []);
    } catch (e) {
      logger.error("Daily ideas error", e);
      toast.error(formatError(e.response?.data?.detail) || "Could not generate ideas");
    } finally {
      setLoadingI(false);
    }
  };

  useEffect(() => {
    // auto-load trends for the default selection once on mount
    loadTrends();
  }, []);

  const sendToGenerate = (title) => {
    sessionStorage.setItem("cf_prefill_topic", title);
    nav("/generate");
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="trend-radar-page">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 text-orange-600 px-3 py-1 text-xs font-bold tracking-wider uppercase mb-3">
            <Flame className="w-3.5 h-3.5" /> Live
          </div>
          <h1 className="text-4xl font-black tracking-tighter text-zinc-950 dark:text-white font-heading">
            Trend Radar
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl">
            What's breaking on {platform} this week — and exactly what to post today, written in your voice.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-8 pb-6 border-b border-zinc-200 dark:border-zinc-800">
          <div>
            <label className="text-xs font-bold text-zinc-400 uppercase block mb-1">Platform</label>
            <div className="flex gap-1">
              {PLATFORMS.map((p) => (
                <button
                  key={p}
                  onClick={() => setPlatform(p)}
                  data-testid={`trends-platform-${p}`}
                  className={`px-3 py-2 text-xs font-semibold border ${platform === p ? "bg-brand text-white border-brand" : "bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700"}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs font-bold text-zinc-400 uppercase block mb-1">Niche (optional)</label>
            <input
              data-testid="trends-niche-input"
              value={niche}
              onChange={(e) => setNiche(e.target.value)}
              placeholder="e.g. day trading, skincare, indie dev"
              className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-sm text-zinc-900 dark:text-white outline-none focus:border-brand"
            />
          </div>
          <button
            onClick={loadTrends}
            disabled={loadingT}
            data-testid="trends-refresh-btn"
            className="bg-brand text-white font-semibold px-5 py-2 text-sm hover:bg-brand-hover disabled:opacity-50 flex items-center gap-2"
          >
            {loadingT ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
            Scan
          </button>
        </div>

        <section className="mb-12">
          <h2 className="text-xl font-bold text-zinc-950 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-brand" /> Trending this week
          </h2>
          {loadingT && (
            <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</div>
          )}
          {!loadingT && trends.length === 0 && (
            <p className="text-sm text-zinc-400">No trends loaded. Hit <span className="font-semibold">Scan</span>.</p>
          )}
          <div className="grid md:grid-cols-2 gap-3" data-testid="trends-list">
            {trends.map((t, i) => (
              <div key={i} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4 hover:border-brand transition-colors">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-bold text-zinc-950 dark:text-white text-sm">{t.title}</h3>
                  {t.difficulty && (
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${t.difficulty === "easy" ? "bg-green-500/10 text-green-600" : t.difficulty === "hard" ? "bg-red-500/10 text-red-600" : "bg-amber-500/10 text-amber-600"}`}>
                      {t.difficulty}
                    </span>
                  )}
                </div>
                {t.why_trending && <p className="text-xs text-zinc-500 mb-2">{t.why_trending}</p>}
                {t.angle && (
                  <p className="text-xs text-zinc-700 dark:text-zinc-300 italic mb-2">
                    Angle: {t.angle}
                  </p>
                )}
                <div className="flex items-center justify-between mt-3">
                  {t.estimated_halflife_days && (
                    <span className="text-[10px] text-zinc-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> ~{t.estimated_halflife_days}d left
                    </span>
                  )}
                  <button
                    onClick={() => sendToGenerate(t.title)}
                    className="text-xs font-semibold text-brand hover:underline"
                    data-testid={`trend-generate-${i}`}
                  >
                    Generate → 
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-zinc-950 dark:text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-brand" /> What to post today
            </h2>
            <button
              onClick={loadIdeas}
              disabled={loadingI}
              data-testid="daily-ideas-btn"
              className="bg-zinc-950 dark:bg-white dark:text-zinc-950 text-white text-sm font-semibold px-4 py-2 hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {loadingI ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkle />}
              Get 3 ideas
            </button>
          </div>
          {ideas.length === 0 && !loadingI && (
            <p className="text-sm text-zinc-400">Tap <span className="font-semibold">Get 3 ideas</span> and we'll propose 3 posts for today, in your voice.</p>
          )}
          <div className="space-y-3" data-testid="daily-ideas-list">
            {ideas.map((idea, i) => (
              <div key={i} className="bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-900 dark:to-zinc-950 border border-zinc-200 dark:border-zinc-800 p-5">
                <p className="text-[10px] font-bold text-zinc-400 tracking-wider uppercase mb-1">Idea {i + 1}</p>
                <h3 className="font-black text-lg text-zinc-950 dark:text-white mb-1">{idea.title}</h3>
                {idea.hook && (
                  <p className="text-sm text-brand italic mb-3">"{idea.hook}"</p>
                )}
                {idea.body_outline && (
                  <ul className="text-sm text-zinc-700 dark:text-zinc-300 space-y-1 mb-3 list-disc list-inside">
                    {idea.body_outline.map((b, j) => <li key={j}>{b}</li>)}
                  </ul>
                )}
                <div className="flex flex-wrap gap-1 mb-3">
                  {(idea.suggested_hashtags || []).map((h, j) => (
                    <span key={j} className="text-[11px] text-brand bg-brand/5 px-1.5 py-0.5 font-semibold">
                      {String(h).startsWith("#") ? h : `#${h}`}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  {idea.estimated_effort_minutes && (
                    <span className="text-xs text-zinc-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> ~{idea.estimated_effort_minutes} min to shoot
                    </span>
                  )}
                  <button
                    onClick={() => sendToGenerate(idea.title)}
                    className="text-xs font-bold text-white bg-brand px-3 py-1.5 hover:bg-brand-hover"
                    data-testid={`idea-generate-${i}`}
                  >
                    Generate full script →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </AppShell>
  );
}

// Small fallback icon (avoid extra lucide import conflict with Sparkles used elsewhere)
function Sparkle() {
  return <Zap className="w-4 h-4" />;
}
